import React, {
  Children,
  cloneElement,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { useSlug } from '../hooks/useSlug';
import { Slider } from '../Slider';
import { GlobalProps } from '../types';
import { classes } from '../utils';

import styles from './styles.module.css';

const ACTIVE_TAB_SELECTOR = `[data-active-tab="true"]`;

type TabProps = {
  title: string;
  isActive?: boolean;
  isDefault?: boolean;
  buttonClassName?: string;
  children: React.ReactNode | React.ReactNode[];
  className?: string;
  Icon?: React.ReactNode;
};

function Tab({ children, isActive, className }: TabProps) {
  return (
    <div
      className={classes([styles.tab, className, isActive && styles.active])}
    >
      {children}
    </div>
  );
}

export function useTabDimensions(
  tabElementRef: React.RefObject<HTMLElement>,
  activeTabSelector: string,
  dependencies: React.DependencyList
) {
  const [gliderWidth, setGliderWidth] = useState<number>(0);
  const [gliderOffset, setGliderOffset] = useState<number>(0);

  useEffect(() => {
    if (!tabElementRef?.current) {
      return;
    }

    const nextActiveTabIndex = tabElementRef.current.querySelector(
      activeTabSelector
    ) as HTMLButtonElement;

    if (!nextActiveTabIndex) {
      return;
    }
    setGliderWidth(nextActiveTabIndex.offsetWidth);
    setGliderOffset(nextActiveTabIndex.offsetLeft);
  }, [activeTabSelector, ...dependencies]);

  return { gliderWidth, gliderOffset };
}

type TabsProps = GlobalProps & {
  children: React.ReactElement<TabProps>[];
  onTabSwitch?: (currentTab: string, tabIndex?: number) => void;
  tabClassName?: string;
  variant?: 'default' | 'basic' | 'box' | 'gliding';
  size?: 'default' | 'small' | 'large';
  width?: string | number;
  showSlider?: boolean;
  trackClick?: (eventAction: string, eventLabel: string) => void;
  textAlignment?: 'left' | 'center';
};

const Tabs = React.forwardRef<HTMLDivElement, TabsProps>(function TabsComponent(
  props,
  ref
) {
  const {
    name = 'Tabs',
    onTabSwitch,
    children = [],
    tabClassName,
    variant = 'default',
    size = 'default',
    width,
    showSlider,
    trackClick,
    textAlignment = 'left',
  } = props;
  const tabNavigation = useRef<HTMLElement>(null);
  const tabHeaders = Children.map(
    children,
    ({
      props: { title: tabTitle, Icon, buttonClassName: className },
    }: React.ReactElement<TabProps>) => ({
      tabTitle,
      Icon,
      className,
    })
  );
  const defaultTabIndex = Children.map(
    children,
    (
      { props: { title: tabTitle, isDefault } }: React.ReactElement<TabProps>,
      index: number
    ) => (isDefault && tabTitle ? index : -1)
  ).find((idx) => idx > -1);

  const [activeTabIndex, setActiveTabIndex] = useState<number>(
    defaultTabIndex || 0
  );

  useEffect(() => {
    if (defaultTabIndex !== undefined) {
      setActiveTabIndex(defaultTabIndex);
    }
  }, [defaultTabIndex]);

  const [componentName] = useSlug(name);
  const handleTabClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      const nextTabTitle = e.currentTarget.dataset.key as string;
      const nextIndex = Number(e.currentTarget.dataset.index);

      setActiveTabIndex(nextIndex);

      if (onTabSwitch) {
        onTabSwitch(nextTabTitle, nextIndex);
      }
    },
    [onTabSwitch, setActiveTabIndex]
  );

  const tabHeaderBar = tabHeaders.map((tabHeader, index: number) => (
    <button
      className={classes([
        styles.navigationItem,
        index === activeTabIndex && styles.active,
        !width && styles.noWidth,
        tabHeader.className,
      ])}
      type="button"
      key={`${tabHeader.tabTitle}-${index}`}
      data-key={tabHeader.tabTitle}
      data-index={index}
      onClick={handleTabClick}
      data-active-tab={index === activeTabIndex}
      style={{ width: width || 'auto' }}
    >
      {tabHeader.Icon && <>{tabHeader.Icon}</>}
      <div
        className={classes([styles.tabText, tabHeader.Icon && styles.tabIcon])}
        data-tab-title={tabHeader.tabTitle}
        data-component={`${componentName}-tab-title`}
      >
        {tabHeader.tabTitle}
      </div>
    </button>
  ));

  const { gliderWidth, gliderOffset } = useTabDimensions(
    tabNavigation,
    ACTIVE_TAB_SELECTOR,
    [activeTabIndex || 0, variant, size]
  );

  return (
    <div
      className={classes(
        [
          styles.tabs,
          variant === 'default' && styles.default,
          variant === 'basic' && styles.basic,
          variant === 'box' && styles.box,
          variant === 'gliding' && styles.gliding,
        ],
        {
          unstyled: props.unstyled,
          className: props.className,
          name: props.name,
        }
      )}
      ref={ref}
      data-component={componentName}
    >
      <nav
        ref={tabNavigation}
        style={
          variant === 'gliding'
            ? ({
                '--glider-width': `${gliderWidth}px`,
                '--glider-offset': `${gliderOffset}px`,
              } as React.CSSProperties)
            : undefined
        }
        className={classes([
          styles.navigation,
          size === 'default' && styles.sizeDefault,
          size === 'small' && styles.sizeSmall,
          size === 'large' && styles.sizeLarge,
          textAlignment === 'center' && styles.centerTabText,
        ])}
      >
        {showSlider ? (
          <Slider
            className={styles.slider}
            trackClick={trackClick}
            shadow={false}
            boxShadow={true}
          >
            {tabHeaderBar}
          </Slider>
        ) : (
          tabHeaderBar
        )}
      </nav>

      {Children.map(
        children,
        (child: React.ReactElement<TabProps>, index: number) =>
          cloneElement(child, {
            isActive: index === activeTabIndex,
            className: classes([tabClassName, child.props.className]),
          })
      )}
    </div>
  );
});

export { Tab, Tabs };
